'use client';

import { Container } from '@/components/container';

export default function PrivacyPolicy() {
  return (
    <section className="p-8 w-full mx-auto bg-gray-50 min-h-screen">
      <Container>
        <div className="py-8 flex flex-col gap-8 max-w-2xl mx-auto">
          <h1 className="font-bold text-3xl tracking-tight mb-2">
            Privacy Policy
          </h1>

          <Section title="Introduction">
            <p>
              At InnovationHUBSolutions, our commitment is to maintain the trust
              and confidence of our users. This Privacy Policy outlines our
              policies and procedures on the collection, use, disclosure, and
              protection of your information when you use our website or
              services, ensuring transparency and giving you control over your
              personal information.
            </p>
          </Section>

          <Section title="1. Information We Collect">
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>
                <b>Account and Profile Information:</b> We collect your name,
                email address, job title, and other profile information when you
                register an account. You may also choose to provide additional
                details such as your professional background and interests.
              </li>
              <li>
                <b>Content You Provide:</b> The content you post, including
                questions, answers, and comments, is collected under your
                ownership, with us retaining a non-exclusive license to use it
                as part of our service operations.
              </li>
              <li>
                <b>Engagement Data:</b> We track your activities on our platform
                to enhance service functionality and tailor features to meet
                your needs.
              </li>
              <li>
                <b>Technical and Device Information:</b> Data collected includes
                device type, browser, IP addresses, and the pages you visit,
                ensuring the security and optimization of our platform.
              </li>
            </ul>
          </Section>

          <Section title="2. How We Use Your Information">
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>
                <b>Service Operation and Enhancement:</b> Your information
                supports the operation, maintenance, and continuous improvement
                of our services, including customer support and personalized
                features.
              </li>
              <li>
                <b>Communication:</b> We communicate with you regarding updates,
                changes, and other service-related notices.
              </li>
              <li>
                <b>Research and Development:</b> We analyze data to enhance the
                platform's functionality and security.
              </li>
              <li>
                <b>Automated Decision-Making and Profiling:</b> Automated
                processes may be used to improve user experience, subject to
                your rights to understand and contest such processes.
              </li>
            </ul>
          </Section>

          <Section title="3. Sharing of Your Information">
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>
                <b>Service Providers:</b> We collaborate with third parties who
                assist in the operation of our services under strict
                confidentiality terms.
              </li>
              <li>
                <b>Legal Obligations and Business Transfers:</b> We disclose
                information as required by law or during business acquisitions.
              </li>
              <li>
                <b>User Consent:</b> Data is shared with third parties only with
                your explicit consent, ensuring sensitive information is handled
                with extra care.
              </li>
            </ul>
          </Section>

          <Section title="4. Your Choices and Controls">
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>
                <b>Data Management:</b> You can review, update, export, and
                delete your personal data via your account settings, giving you
                complete control over your information.
              </li>
              <li>
                <b>Communication Preferences:</b> You have the option to manage
                how and when you receive communications from us.
              </li>
              <li>
                <b>
                  Deletion of User Accounts By Admin and Retention of Posted
                  Content:
                </b>{' '}
                If your account is deleted by an administrator due to policy
                violations, inactivity, or upon your request, the content you
                previously contributed (including questions, answers, comments,
                or stories) will remain visible on our platform attributed to
                your name. If you prefer your posted content to be removed from
                the platform entirely, please contact our support team at{' '}
                <a
                  href="mailto:privacy@innovationhubsolutions.com"
                  className="text-blue-600 underline"
                >
                  privacy@innovationhubsolutions.com
                </a>
                . We will process your request promptly, respecting your right
                to control your personal contributions and maintaining
                transparency about content visibility after account deletion.
              </li>
              <li>
                <b>User Initiated Deletion and Retention of Posted Content:</b>{' '}
                If you choose to delete your account yourself, your previously
                contributed content (including questions, comments, or stories)
                will remain visible on the platform and attributed to 'Deleted
                User.' If you would prefer to have your content removed
                entirely, please contact our support team at{' '}
                <a
                  href="mailto:privacy@innovationhubsolutions.com"
                  className="text-blue-600 underline"
                >
                  privacy@innovationhubsolutions.com
                </a>
                .
              </li>
            </ul>
          </Section>

          <Section title="5. Security Measures">
            <p>
              <b>Robust Protections:</b> Our security measures, including
              encryption and firewalls, protect your information from
              unauthorized access and potential breaches.
            </p>
          </Section>

          <Section title="6. International Data Transfers">
            <p>
              Your information may be transferred and processed in different
              countries, always under strict safeguards.
            </p>
          </Section>

          <Section title="7. Data Retention Policy">
            <p>
              We retain data only for as long as necessary for the purposes
              outlined in this policy or as required by law.
            </p>
          </Section>

          <Section title="8. Children and Vulnerable Users">
            <p>
              We do not knowingly collect personal data from children under 16
              and take additional measures to protect vulnerable users.
            </p>
          </Section>

          <Section title="9. Changes to This Policy">
            <p>
              We periodically update our Privacy Policy and will notify you of
              any significant changes.
            </p>
          </Section>

          <Section title="10. Contact Us">
            <p>
              For questions or concerns regarding this policy or your data,
              please contact our Data Protection Officer at{' '}
              <a
                href="mailto:privacy@innovationhubsolutions.com"
                className="text-blue-600 underline"
              >
                privacy@innovationhubsolutions.com
              </a>
              .
            </p>
          </Section>

          <Section title="11. Link to Terms of Service">
            <p>
              Our Terms of Service can be found{' '}
              <a href="/terms-of-service" className="text-blue-600 underline">
                here
              </a>
              . This Privacy Policy is an integral part of these terms.
            </p>
          </Section>

          <Section title="Data Management and User Control:">
            <p>
              <b>Incorporated:</b> The policy explicitly states that users can
              review, update, export, and delete their data through account
              settings, providing users with extensive control over their
              personal information.
            </p>
          </Section>

          <Section title="Detailed Description of Data Collection:">
            <p>
              <b>Incorporated:</b> The policy clearly describes the types of
              information collected (such as Account and Profile Information,
              Content Provided, Engagement Data, and Technical and Device
              Information) and the purposes for which this information is used,
              enhancing transparency and building user trust.
            </p>
          </Section>

          <Section title="Use of Information for Service Improvement:">
            <p>
              <b>Incorporated:</b> The policy specifies that the data collected
              is used to operate, maintain, and improve services, including
              personalized features and research and development, aligning with
              how user data enhances functionalities.
            </p>
          </Section>

          <Section title="Sharing of Information:">
            <p>
              <b>Incorporated:</b> Details on how information is shared with
              third-party service providers, under legal obligations, and with
              user consent are included. It emphasizes that data is shared only
              under strict confidentiality agreements and with explicit user
              consent for sensitive information.
            </p>
          </Section>

          <Section title="User Consent:">
            <p>
              <b>Incorporated:</b> The policy requires user consent for sharing
              data outside of standard operations, especially for sensitive
              personal information, ensuring compliance with data protection
              regulations like GDPR.
            </p>
          </Section>

          <Section title="Information on Security Measures:">
            <p>
              <b>Incorporated:</b> The policy outlines the security measures in
              place, such as encryption and firewalls, to protect user data from
              unauthorized access and breaches.
            </p>
          </Section>

          <Section title="Policy on Automated Decision-Making:">
            <p>
              <b>Incorporated:</b> The policy includes a section on automated
              decision-making and profiling, explaining how these processes are
              used to improve user experience and ensuring users have the right
              to understand and object to decisions made without human
              intervention.
            </p>
          </Section>

          <p>
            The final version of the IHQX Privacy Policy thoroughly incorporates
            the elements inspired by Google's privacy policy, enhancing the
            document's clarity, user-friendliness, and compliance with data
            protection standards. This integration not only aligns with industry
            best practices but also significantly boosts the policy's
            transparency and the users' trust and control over their data.
          </p>
        </div>
      </Container>
    </section>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-6">
      <h2 className="font-semibold text-xl mb-2 text-gray-900">{title}</h2>
      <div className="text-gray-700 space-y-2">{children}</div>
    </section>
  );
}
